import { Component, computed, inject } from '@angular/core';

import { AuthStore } from '@sandbox-platform/shared-ui';
import { PERMISSIONS } from '@sandbox-platform/shared-ui';
import {
  QuickActionCardComponent,
  QuickActionData,
} from '@sandbox-platform/shared-ui';

@Component({
  standalone: true,
  imports: [QuickActionCardComponent],
  selector: 'lib-quick-actions-section',
  template: `
    @if (visibleQuickActions().length) {
    <div class="flex flex-col gap-y-2">
      <div class="fade-in-header">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-2">
          Quick Actions
        </h3>
      </div>

      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 quick-actions-grid"
      >
        @for ( action of visibleQuickActions(); track action.title; let i =
        $index ) {
        <div
          class="fade-in-action"
          [style.animation-delay]="i * 0.1 + 0.2 + 's'"
        >
          <lib-quick-action-card [data]="action" />
        </div>
        }
      </div>
    </div>
    }

    <style>
      .fade-in-header {
        opacity: 0;
        transform: translateY(10px);
        animation: slideInUp 0.5s ease-out forwards;
      }

      .fade-in-action {
        opacity: 0;
        transform: translateY(20px) scale(0.95);
        animation: slideInUpScale 0.6s ease-out forwards;
      }

      .quick-actions-grid {
        animation: containerFadeIn 0.8s ease-out forwards;
      }

      @keyframes slideInUp {
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @keyframes slideInUpScale {
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      @keyframes containerFadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      /* Enhanced hover effects */
      .quick-actions-grid > div:hover {
        transform: translateY(-3px);
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }

      /* Focus and accessibility improvements */
      .quick-actions-grid > div:focus-within {
        transform: translateY(-2px);
        transition: transform 0.2s ease-out;
      }
    </style>
  `,
})
export class QuickActionsSectionComponent {
  private readonly authStore = inject(AuthStore);

  private readonly quickActions: QuickActionData[] = [
    {
      title: 'View Experts',
      description: 'Browse and manage expert profiles',
      icon: 'M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z',
      link: '/expert',
      permissionRoute: '/expert',
    },
    {
      title: 'Add Expert',
      description: 'Register a new expert in the system',
      icon: 'M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z',
      link: '/expert/add',
      requiredPermissions: [PERMISSIONS.EXPERT.EXPERTS_CREATE],
    },
    {
      title: 'View Attendance',
      description: 'View and manage attendance records',
      icon: 'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m-6 9l2 2 4-4',
      link: '/attendance',
      requiredPermissions: [PERMISSIONS.EXPERT.ATTENDANCE_READ],
    },
    {
      title: 'View Referrals',
      description: 'Browse and manage referral records',
      icon: 'M8 7h8m-8 4h8m-8 4h5M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z',
      link: '/referrals',
      permissionRoute: '/referrals',
    },
  ];

  protected readonly visibleQuickActions = computed(() =>
    this.quickActions.filter((action) => this.canDisplayAction(action))
  );

  private canDisplayAction(action: QuickActionData): boolean {
    if (action.requiredPermissions?.length) {
      if (action.requireAllPermissions) {
        return this.authStore.hasExpertPermission(action.requiredPermissions);
      }

      return this.authStore.hasAnyExpertPermission(action.requiredPermissions);
    }

    if (action.permissionRoute) {
      return this.authStore.canAccessExpertRoute(action.permissionRoute);
    }

    if (action.link && !action.is_external_link) {
      return this.authStore.canAccessExpertRoute(action.link);
    }

    // Default to true if no explicit permission requirements are set
    return true;
  }
}
